import React, {useState} from "react";
import {Alert, Box, Button, Chip, Link, Stack, Typography} from "@mui/material";
import {ChevronRight as ChevronRightIcon} from "@mui/icons-material";
import {GRAUS, completude, correcaoNoManifesto, pendenciasPorGrau} from "../sistemaPagina";
import {CompletudeChip, iconeDaCor} from "./SistemaVisual";
import SecaoCard from "./SecaoCard";

const LIMITE_POR_GRAU = 5;
const NOME_DA_SECAO = {catalogo: 'Catálogo', integracao: 'Integração'};

const regraDoStatus = (status) => {
    if (status === 'INTERNO') return 'Uso interno: o sistema não entra em planos. A lista serve de checagem do catálogo.';
    if (status === 'DESENVOLVIMENTO') return 'Nada aqui impede salvar. O que bloqueia impede pôr o sistema à venda.';
    return 'Nada aqui impede salvar. Sistema ou módulo com pendência que bloqueia não deveria entrar em plano novo.';
};

// Onde o item está: o módulo da funcionalidade, ou a seção quando o alvo é o sistema.
const escopoDe = (item, modulos) => {
    if (item.alvo?.tipo === 'funcionalidade') {
        const modulo = modulos.find((m) => m.id === item.alvo.modulo_id);
        return modulo ? `Módulo ${modulo.nome}` : '';
    }
    if (item.alvo?.tipo === 'sistema') return NOME_DA_SECAO[item.secao] || '';
    return '';
};

const ItemPendencia = ({item, cor, modulos, onIr}) => {
    const escopo = escopoDe(item, modulos);
    const noManifesto = correcaoNoManifesto(item);
    return (
        <Stack component="li" direction="row" spacing={1.5} alignItems="flex-start" sx={{py: 1.25, borderTop: 1, borderColor: 'divider', '&:first-of-type': {borderTop: 0}}}>
            <Box sx={{color: `${cor}.main`, display: 'flex', pt: 0.25}}>{iconeDaCor(cor)}</Box>
            <Box sx={{flexGrow: 1, minWidth: 0}}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" fontWeight={600}>{item.mensagem}</Typography>
                    {escopo ? <Chip size="small" variant="outlined" label={escopo} /> : null}
                </Stack>
                {item.dica || noManifesto ? (
                    <Typography variant="caption" color="text.secondary" component="div">
                        {item.dica}
                        {noManifesto ? (
                            <>{item.dica ? ' ' : ''}{noManifesto} <Box component="span" sx={{fontFamily: 'monospace'}}>{item.manifesto}</Box>.</>
                        ) : null}
                    </Typography>
                ) : null}
            </Box>
            <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => onIr(item)} aria-label={`Ir para: ${item.mensagem}`} sx={{flexShrink: 0}}>
                Ir
            </Button>
        </Stack>
    );
};

const GrupoDePendencias = ({grau, itens, modulos, onIr}) => {
    const [todos, setTodos] = useState(false);
    const visiveis = todos ? itens : itens.slice(0, LIMITE_POR_GRAU);
    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 0.5}}>
                <Typography variant="overline" color="text.secondary" sx={{lineHeight: 1.5}}>{grau.titulo}</Typography>
                <Chip size="small" color={grau.cor} label={itens.length} />
            </Stack>
            <Box component="ul" sx={{listStyle: 'none', p: 0, m: 0}}>
                {visiveis.map((item) => (
                    <ItemPendencia key={`${item.codigo}-${item.alvo?.id}`} item={item} cor={grau.cor} modulos={modulos} onIr={onIr} />
                ))}
            </Box>
            {itens.length > LIMITE_POR_GRAU ? (
                <Link component="button" type="button" variant="body2" onClick={() => setTodos((atual) => !atual)}>
                    {todos ? 'Mostrar menos' : `Mostrar mais ${itens.length - LIMITE_POR_GRAU}`}
                </Link>
            ) : null}
        </Box>
    );
};

export default function PendenciasSecao({sistema, pendencias, modulos, onIr}) {
    const porGrau = pendenciasPorGrau(pendencias);
    const vazio = pendencias && !pendencias.itens?.length;
    return (
        <SecaoCard
            id="pendencias"
            titulo="Pendências"
            subtitulo={regraDoStatus(sistema.status)}
            chip={<CompletudeChip completude={completude('pendencias', {sistema, pendencias})} />}
        >
            <Stack spacing={2}>
                {!pendencias ? <Typography color="text.secondary">Carregando as pendências…</Typography> : null}
                {vazio ? (
                    <Alert severity="success" variant="outlined">
                        <strong>Nada pendente.</strong> Todo módulo tem funcionalidade completa{sistema.status === 'INTERNO' ? '' : ', e a integração está preenchida'}.
                    </Alert>
                ) : null}
                {GRAUS.map((grau) => (porGrau[grau.id].length ? (
                    <GrupoDePendencias key={grau.id} grau={grau} itens={porGrau[grau.id]} modulos={modulos} onIr={onIr} />
                ) : null))}
                {sistema.status !== 'INTERNO' ? (
                    <Typography variant="caption" color="text.secondary">
                        Métrica apontada e código de serviço ainda não entram nas pendências: não há dado para eles.
                    </Typography>
                ) : null}
            </Stack>
        </SecaoCard>
    );
}
